import type { RunStateKey } from "./runStates";
import type { IconColor, IconName } from "@platform/design-system";
import type { ChipProps } from "@mui/material";

export interface RunStateTreatment {
  readonly icon: IconName;
  readonly iconColor: IconColor;
  readonly chipColor: ChipProps["color"];
  /**
   * A theme palette path for tinted text, given separately because
   * `chipColor` is a Chip prop rather than a palette key: `not-reported` is
   * `default`, and `default.main` resolves to nothing.
   */
  readonly textColor: string;
}

/**
 * How each status word looks.
 *
 * The Launchpad reads the three live states as a severity ramp — green, amber,
 * red. The Build tab colours the same words differently, showing Awaiting
 * Input as primary and Partially Extracted as info, because there a status is
 * something you set rather than something to triage. The words are shared
 * (see `runStates.ts`); the treatment is not.
 */
export const RUN_STATE_TREATMENTS: Record<RunStateKey, RunStateTreatment> = {
  complete: {
    icon: "success",
    iconColor: "success",
    chipColor: "success",
    textColor: "success.main",
  },
  "partially-pre-populated": {
    icon: "warning",
    iconColor: "warning",
    chipColor: "warning",
    textColor: "warning.main",
  },
  "awaiting-input": {
    icon: "error",
    iconColor: "error",
    chipColor: "error",
    textColor: "error.main",
  },
  "not-reported": {
    icon: "remove",
    iconColor: "muted",
    chipColor: "default",
    textColor: "text.secondary",
  },
};
